import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { opsFetch } from "../lib/api";
import { Field, Pill } from "../components/ui";

type Op = { id: string; name: string; username?: string | null; role: string; active: boolean };
type ListRes = { data: { items: Op[] } };
type CreateRes = { data: { id: string; name: string; role: string; api_key: string } };

export function OperatorsPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["ops-operators"],
    queryFn: () => opsFetch<ListRes>("/api/ops/operators"),
  });

  const create = useMutation({
    mutationFn: () =>
      opsFetch<CreateRes>("/api/ops/operators", {
        method: "POST",
        body: JSON.stringify({
          name,
          role,
          username: username.trim() || undefined,
          password: password || undefined,
        }),
      }),
    onSuccess: (res) => {
      setCreatedKey(res.data.api_key);
      setName("");
      setUsername("");
      setPassword("");
      void qc.invalidateQueries({ queryKey: ["ops-operators"] });
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      opsFetch(`/api/ops/operators/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ active }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-operators"] }),
  });

  function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate();
  }

  return (
    <>
      <form className="ops-panel ops-form" onSubmit={onCreate}>
        <h3 className="ops-panel__title">New operator</h3>
        <div className="ops-form__grid">
          <Field label="Name">
            <input className="ops-input" onChange={(e) => setName(e.target.value)} required value={name} />
          </Field>
          <Field label="Username">
            <input className="ops-input" onChange={(e) => setUsername(e.target.value)} value={username} />
          </Field>
          <Field label="Password">
            <input className="ops-input" onChange={(e) => setPassword(e.target.value)} type="password" value={password} />
          </Field>
          <Field label="Role">
            <select className="ops-input" onChange={(e) => setRole(e.target.value)} value={role}>
              <option value="viewer">viewer</option>
              <option value="affiliate">affiliate</option>
              <option value="editor">editor</option>
              <option value="admin">admin</option>
            </select>
          </Field>
        </div>
        <button className="ops-btn ops-btn--primary" disabled={create.isPending} type="submit">
          Create
        </button>
      </form>
      {createdKey ? (
        <p className="ops-panel ops-ok">
          New API key (copy now): <code>{createdKey}</code>
        </p>
      ) : null}
      {q.isError ? <p className="ops-error">{(q.error as Error).message}</p> : null}
      {q.data ? (
        <div className="ops-table-wrap ops-panel">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.data.items.map((op) => (
                <tr key={op.id}>
                  <td>{op.name}</td>
                  <td>{op.username || "—"}</td>
                  <td><Pill value={op.role} /></td>
                  <td><Pill value={op.active ? "active" : "suspended"} /></td>
                  <td>
                    <button
                      className="ops-btn ops-btn--ghost"
                      onClick={() => patch.mutate({ id: op.id, active: !op.active })}
                      type="button"
                    >
                      {op.active ? "Disable" : "Enable"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
